// ./src/components/features/Events/controls/guestModeConfig.ts
//
// Shared constants for "how guests join" — kept separate from the React
// components so GuestModeField and GuestModeCallout can both import them without
// a circular dependency.

export const GUEST_MODE = {
  LINK_RSVP: 'link_rsvp',
  MANAGED_INVITES: 'managed_invites',
  SAVE_THE_DATE: 'save_the_date',
  TICKETED: 'ticketed',
} as const;

export const DEFAULT_GUEST_MODE = GUEST_MODE.LINK_RSVP;

/**
 * Segmented-control options. Ordered by increasing commitment — announce →
 * invite → open → sell. The DEFAULT_GUEST_MODE (Open RSVP) stays pre-selected
 * regardless of order. The per-mode explanation lives in GuestModeCallout.
 */
export const GUEST_MODES: { value: string; label: string }[] = [
  { value: GUEST_MODE.SAVE_THE_DATE, label: 'Save the date' },
  { value: GUEST_MODE.MANAGED_INVITES, label: 'Invite list' },
  { value: GUEST_MODE.LINK_RSVP, label: 'Open RSVP' },
  { value: GUEST_MODE.TICKETED, label: 'Tickets' },
];

/**
 * Optional YouTube walkthrough per mode — embedded under the explanation in
 * GuestModeCallout when set. Fill these in as recordings are published; any
 * standard youtube.com/watch, youtu.be or /embed URL works. Empty = no video.
 */
export const GUEST_MODE_VIDEOS: Record<string, string> = {
  [GUEST_MODE.SAVE_THE_DATE]: '',
  [GUEST_MODE.MANAGED_INVITES]: '',
  [GUEST_MODE.LINK_RSVP]: '',
  [GUEST_MODE.TICKETED]: '',
};

/**
 * Normalize a YouTube URL (watch / youtu.be / shorts / embed) to an embeddable
 * URL. Returns null if it isn't a recognizable YouTube link.
 */
export const toYouTubeEmbed = (url?: string | null): string | null => {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  // Already an embed URL.
  const embedMatch = trimmed.match(/youtube\.com\/embed\/([\w-]{11})/);
  if (embedMatch) return `https://www.youtube.com/embed/${embedMatch[1]}`;

  // youtu.be/<id>
  const shortMatch = trimmed.match(/youtu\.be\/([\w-]{11})/);
  if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;

  // youtube.com/shorts/<id>
  const shortsMatch = trimmed.match(/youtube\.com\/shorts\/([\w-]{11})/);
  if (shortsMatch) return `https://www.youtube.com/embed/${shortsMatch[1]}`;

  // youtube.com/watch?v=<id>
  const watchMatch = trimmed.match(/[?&]v=([\w-]{11})/);
  if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;

  return null;
};
